import { CreateUserAccountWithDefaultGroupRequestV1, UserAccountFormModel } from '@access-management/access-management';
import { AccountsTableComponent } from '@access-management/accounts-table/accounts-table';
import { UsersTableComponent } from '@access-management/users-table/users-table';
import { Component, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { firstValueFrom } from 'rxjs';

import { AccessManagementService } from '@core/services/access-management';

@Component({
  selector: 'app-accounts-users-page',
  imports: [
    AccountsTableComponent,
    ButtonModule,
    DialogModule,
    FormsModule,
    IconField,
    InputIcon,
    InputTextModule,
    UsersTableComponent,
    FormField
  ],
  templateUrl: './accounts-users-page.html',
  styleUrl: './accounts-users-page.scss',
  standalone: true
})
export class AccountsUsersPage {
  private readonly accessManagementService = inject(AccessManagementService);
  private readonly messageService = inject(MessageService);

  private readonly accountsTable = viewChild<AccountsTableComponent>(AccountsTableComponent);
  private readonly usersTable = viewChild<UsersTableComponent>(UsersTableComponent);

  readonly showDialog = signal<boolean>(false);
  readonly userAccountFormModel = signal<UserAccountFormModel>({
    externalId: '',
    email: '',
    name: '',
    groupName: '',
    groupDescription: '',
    accountName: '',
    accountDescription: ''
  });

  readonly saving = signal<boolean>(false);
  readonly searchTerm = signal<string>('');

  readonly userAccountForm = form(this.userAccountFormModel, (schemaPath) => {
    required(schemaPath.externalId, { message: 'External ID is required' });
    required(schemaPath.email, { message: 'Email is required' });
    required(schemaPath.groupName, { message: 'Group Name is required' });
    required(schemaPath.accountName, { message: 'Account Name is required' });
  });

  onCreateUserAccount(): void {
    this.userAccountFormModel.set({
      externalId: '',
      email: '',
      name: '',
      groupName: '',
      groupDescription: '',
      accountName: '',
      accountDescription: ''
    });
    this.showDialog.set(true);
  }

  isFormValid(): boolean {
    const model = this.userAccountFormModel();
    return !!(
      model.externalId.trim() &&
      model.email.trim() &&
      this.userAccountForm.externalId().valid() &&
      this.userAccountForm.email().valid() &&
      this.userAccountForm.groupName().valid() &&
      this.userAccountForm.accountName().valid()
    );
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  async onSaveUserAccount(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    const formData = this.userAccountFormModel();

    const operation = this.accessManagementService.createUserAccount(
      formData as CreateUserAccountWithDefaultGroupRequestV1
    );

    try {
      await firstValueFrom(operation);
      this.showSuccess(`Account and User created successfully`);
      this.showDialog.set(false);
      this.accountsTable()!.accountsResource.reload();
      this.usersTable()!.usersResource.reload();
    } catch (error) {
      this.showError('Failed to save user');
      console.error('Save user error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.accountsTable()!.onSearch(event);
    this.usersTable()!.onSearch(event);
    this.searchTerm.set(value);
  }
}
